import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AlertBox, Button, SvgIcon } from '@/components';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { amlTraceActions, userActions } from '@/redux/actions';
import { amlTraceSelectors, userSelectors } from '@/redux/selectors';
import { AmlTraceRequest, AmlTraceData } from '@/api';
import { showEmailConfirmationToast } from '@/helpers';
import { navigationPages } from '@/config';
import TraceEntryForm from '../TraceEntryForm/TraceEntryForm';
import TraceEntryChip from '../TraceEntryChip/TraceEntryChip';
import { TraceEntry } from './TraceInvestigationForm.types';

import styles from './TraceInvestigationForm.module.sass';

interface TraceInvestigationFormProps {
  isDisabled: boolean;
}

const TraceInvestigationForm = ({
  isDisabled,
}: TraceInvestigationFormProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { loading, error } = useAppSelector(amlTraceSelectors.getTraceSlice);
  const activeUuid = useAppSelector(amlTraceSelectors.getActiveUuid);
  const isEmailConfirmed = useAppSelector(userSelectors.checkIfEmailConfirmed);
  const traceHashFor = useAppSelector(userSelectors.traceHashFor);
  const termsConfirmRequired = useAppSelector(
    userSelectors.checkIfTermsConfirmRequired,
  );

  const [entries, setEntries] = useState<TraceEntry[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(true);
  const [prefillHash, setPrefillHash] = useState('');
  const pendingSubmitRef = useRef(false);
  const prefillConsumedRef = useRef(false);

  const handleConfirmEntry = useCallback(
    (entry: TraceEntry) => {
      setEntries((prev) => {
        if (editingId) {
          return prev.map((e) => (e.id === editingId ? entry : e));
        }
        return [...prev, entry];
      });
      setEditingId(null);
      setIsAdding(false);
      // Consume the prefill: a second entry must start empty.
      setPrefillHash('');
    },
    [editingId],
  );

  const handleRemoveEntry = useCallback((id: string) => {
    setEntries((prev) => {
      const next = prev.filter((e) => e.id !== id);
      if (next.length === 0) {
        setIsAdding(true);
      }
      return next;
    });
  }, []);

  const handleEditEntry = useCallback((id: string) => {
    setEditingId(id);
    setIsAdding(true);
  }, []);

  const handleCancelEntry = useCallback(() => {
    if (editingId) {
      setEntries((prev) => {
        const next = prev.filter((e) => e.id !== editingId);
        setIsAdding(next.length === 0);
        return next;
      });
    } else {
      setIsAdding(false);
    }
    setEditingId(null);
  }, [editingId]);

  const editingEntry = entries.find((e) => e.id === editingId) || null;

  const submitDisabled =
    isDisabled || loading || entries.length === 0 || isAdding;

  const submitHandler = useCallback(() => {
    if (!isEmailConfirmed) {
      showEmailConfirmationToast();
      return;
    }

    if (loading || entries.length === 0) return;

    dispatch(amlTraceActions.setError(null));
    pendingSubmitRef.current = true;

    const traces: AmlTraceData[] = entries.map((entry) => ({
      blockchain: entry.chain,
      asset: entry.displayAsset,
      hash: entry.hash,
      tokenId: entry.tokenId,
    }));

    const payload: AmlTraceRequest = { traces };
    dispatch(amlTraceActions.startTrace(payload));
  }, [dispatch, entries, isEmailConfirmed, loading]);

  useEffect(() => {
    if (!loading && pendingSubmitRef.current && !error && activeUuid) {
      pendingSubmitRef.current = false;
      navigate(navigationPages.TRACE_INVESTIGATION_DETAILS.path, {
        state: {
          uuid: activeUuid,
        },
      });
    }
  }, [loading, error, activeUuid, navigate]);

  // A tx hash pasted on the AI Tracer landing before signup, parked on the user
  // record by verify-otp. Seed the form from it exactly once per mount and clear
  // it server-side. The ref is load-bearing: `discardTraceHashFor` clears the
  // store optimistically even when its POST fails, and the 60s `monitorUser`
  // refetch would then bring the hash back and remount the draft form mid-edit,
  // wiping whatever the user had typed.
  //
  // Held back while the terms modal is up: `RequireAuth` renders this form
  // behind `AgreementModal`, so a new user's effects run under a blocking
  // overlay. Consuming the hash there would discard it before it could be
  // seen, and `confirmTerms` reloads the page, so the reload would land on an
  // already-cleared record and show an empty field.
  useEffect(() => {
    if (!traceHashFor || termsConfirmRequired || prefillConsumedRef.current) {
      return;
    }

    prefillConsumedRef.current = true;
    setPrefillHash(traceHashFor);
    dispatch(userActions.discardTraceHashFor());
  }, [dispatch, traceHashFor, termsConfirmRequired]);

  return (
    <div className={styles.root}>
      {entries
        .filter((entry) => entry.id !== editingId)
        .map((entry) => (
          <TraceEntryChip
            key={entry.id}
            entry={entry}
            onEdit={() => handleEditEntry(entry.id)}
            onRemove={() => handleRemoveEntry(entry.id)}
          />
        ))}

      {isAdding && (
        <TraceEntryForm
          key={editingId || `draft-${prefillHash}`}
          isDisabled={isDisabled}
          onConfirm={handleConfirmEntry}
          onCancel={handleCancelEntry}
          canCancel={entries.length > 0}
          initialValue={editingEntry}
          initialHash={editingId ? undefined : prefillHash}
        />
      )}

      {!isAdding && (
        <button
          type="button"
          className={styles.addAnother}
          onClick={() => setIsAdding(true)}
        >
          <SvgIcon name="plus" />
          {t('translation.ai_investigation_add_another')}
        </button>
      )}

      {error && (
        <AlertBox type="danger" small>
          {{ description: error }}
        </AlertBox>
      )}

      <Button
        corner="rect"
        className={styles.submit}
        onClick={submitHandler}
        disabled={submitDisabled}
      >
        {t('translation.ai_investigation_cta_prefix')}
        <span>|</span>
        <span className={styles.btnIcon}>
          <SvgIcon name="aml" />
          10
        </span>
      </Button>
    </div>
  );
};

export default TraceInvestigationForm;
